import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { listEmbarcacoes } from "./embarcacoes.gateway";
import { DEFAULT_EMBARCACOES_SORT } from "./embarcacoes.types";
import type {
  EmbarcacoesFilters,
  EmbarcacoesQueryState,
  EmbarcacoesSort,
} from "./embarcacoes.types";

/** Espera entre a digitação e a consulta, como na listagem de Usuários. */
export const SEARCH_DEBOUNCE_MS = 300;

/** AC7 — o estado "sem permissão" da tela vem do `403` devolvido pela API. */
export const EMBARCACOES_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar as embarcações do Hub.";
export const EMBARCACOES_ERROR_MESSAGE = "Não foi possível carregar as embarcações.";

const INITIAL_STATE: EmbarcacoesQueryState = {
  status: "loading",
  embarcacoes: [],
  total: 0,
  errorMessage: null,
};

export interface UseEmbarcacoesParams {
  search: string;
  filters: EmbarcacoesFilters;
  page: number;
  pageSize: number;
  sort?: EmbarcacoesSort;
}

interface UseEmbarcacoesResult extends EmbarcacoesQueryState {
  reload: () => void;
}

export function useEmbarcacoes({
  search,
  filters,
  page,
  pageSize,
  sort = DEFAULT_EMBARCACOES_SORT,
}: UseEmbarcacoesParams): UseEmbarcacoesResult {
  const [state, setState] = useState<EmbarcacoesQueryState>(INITIAL_STATE);
  const [reloadToken, setReloadToken] = useState(0);
  const previousTerm = useRef<string | null>(null);

  const term = search.trim();
  const { acronimo, embarcacao, tipo, situacao } = filters;
  const { field: sortField, direction: sortDirection } = sort;

  /**
   * Os filtros são comparados pelo conteúdo: o efeito não pode depender da
   * identidade dos arrays, que muda a cada render do chamador. A chave serializada
   * é só o critério de igualdade — os valores enviados na consulta continuam
   * vindo das listas originais.
   */
  const filtersKey = JSON.stringify([acronimo, embarcacao, tipo, situacao]);
  const appliedFilters = useMemo(
    () => ({ acronimo, embarcacao, tipo, situacao }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtersKey],
  );

  useEffect(() => {
    // Debounce só na digitação; carga inicial, filtros, paginação e retry são imediatos.
    const shouldDebounce = previousTerm.current !== null && previousTerm.current !== term;
    previousTerm.current = term;

    let cancelled = false;
    // Cancela a consulta anterior: paginar ou filtrar de novo descarta a resposta em trânsito.
    const controller = new AbortController();

    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    const timer = setTimeout(
      () => {
        listEmbarcacoes({
          busca: term,
          filters: appliedFilters,
          page,
          pageSize,
          sort: { field: sortField, direction: sortDirection },
          signal: controller.signal,
        })
          .then(({ embarcacoes, total }) => {
            if (cancelled) return;
            setState({ status: "success", embarcacoes, total, errorMessage: null });
          })
          .catch((error: unknown) => {
            if (cancelled) return;

            const isForbidden = isAPIError(error) && error.status === 403;
            setState({
              status: isForbidden ? "forbidden" : "error",
              embarcacoes: [],
              total: 0,
              errorMessage: isForbidden
                ? EMBARCACOES_FORBIDDEN_MESSAGE
                : getErrorMessage(error) || EMBARCACOES_ERROR_MESSAGE,
            });
          });
      },
      shouldDebounce ? SEARCH_DEBOUNCE_MS : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [term, appliedFilters, page, pageSize, sortField, sortDirection, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, reload };
}
